import type { Data, Node, Root } from 'mdast';
import { findAndReplace } from 'mdast-util-find-and-replace';

import type { RichTextElement, RichTextMentionElement } from '../data-contract/chat';

// Mentions are React components, but react-markdown renders from a plain string. To keep tables,
// lists and other GFM structures intact while embedding mentions at their exact position (even
// inside table cells), each mention is replaced with a Private-Use-Area sentinel before parsing.
// PUA characters are treated as ordinary text by remark, so they never break the `|` pipes of a
// GFM table. A remark plugin (`remarkMentions`) then turns the sentinels back into nodes that the
// `span` component renderer resolves to the original mention element.
const SENTINEL_OPEN = '\u{E000}';
const SENTINEL_CLOSE = '\u{E001}';
const SENTINEL_PATTERN = /\u{E000}(\d+)\u{E001}/gu;

/** Element name the mention sentinel becomes in the hast tree (overridden via `components.span`). */
export const MENTION_HOST_ELEMENT = 'span';
/** hast property carrying the index into the `mentions` array passed to the renderer. */
export const MENTION_INDEX_PROPERTY = 'mentionIndex';

type MentionNodeData = Data & {
    readonly hName: typeof MENTION_HOST_ELEMENT;
    readonly hProperties: { readonly [MENTION_INDEX_PROPERTY]: number };
};

interface MentionNode extends Node {
    readonly type: 'mention';
    readonly data: MentionNodeData;
}

declare module 'mdast' {
    interface PhrasingContentMap {
        mention: MentionNode;
    }
    interface RootContentMap {
        mention: MentionNode;
    }
}

export type SerializedRichText = {
    readonly markdown: string;
    readonly mentions: readonly RichTextMentionElement[];
};

export const serializeRichTextToMarkdown = (content: readonly RichTextElement[]): SerializedRichText => {
    const mentions: RichTextMentionElement[] = [];
    const markdown = content
        .map((element) => {
            if (element.type === 'text') return element.text;
            const index = mentions.push(element) - 1;
            return `${SENTINEL_OPEN}${index}${SENTINEL_CLOSE}`;
        })
        .join('');
    return { markdown, mentions };
};

export const remarkMentions =
    () =>
    (tree: Root): undefined => {
        findAndReplace(tree, [
            [
                SENTINEL_PATTERN,
                (_match: string, index: string): MentionNode => ({
                    data: {
                        hName: MENTION_HOST_ELEMENT,
                        hProperties: { [MENTION_INDEX_PROPERTY]: Number(index) },
                    },
                    type: 'mention',
                }),
            ],
        ]);
    };
